import React, { useState, useEffect, useRef } from "react";
import "./Download.scss";
import ProgressBar from "../Common/ProgressBar/ProgressBar";
import { downloadWithProgress } from "../../common/downloadWithProgress";

const API_URL = process.env.REACT_APP_API_URL;


const Download = ({ companyName, authToken }) => {
  const [files, setFiles] = useState([]);
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [downloading, setDownloading] = useState(null);
  const [downloadPercent, setDownloadPercent] = useState(0); // 0..100, or null when size unknown
  const wsRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sort, setSort] = useState({ key: "name", dir: "asc" });

  const isFirmwareMode = companyName?.toLowerCase() === "firmware";

  useEffect(() => {
    const ws = new WebSocket("wss://fdl-service.azurewebsites.net");
    ws.onopen = () => console.log("WebSocket connected");
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === "uploadProgress") {
        setUploadProgress(data.percentComplete);
        if (data.percentComplete === "100.00") fetchFiles();
      }
    };
    ws.onerror = (err) => console.error("WebSocket error:", err);
    wsRef.current = ws;
    return () => ws.close();
  }, []);

    useEffect(() => {
    if (authToken) fetchFiles();   // don't fetch until token is ready
  }, [companyName, authToken]);    // add authToken to deps

  // ── CHANGE 1: fetchFiles now sends the auth token ─────────────────────────
  // Old code used a query param (?companyName=X) and no auth header.
  // New code sends Authorization: Bearer <token> — the server reads the company
  // from the token, so no query param needed. Falls back to unauthenticated
  // only for firmware mode (which has no login gate).
  const fetchFiles = async () => {
    
    try {
      const headers = {};
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      // Firmware mode keeps the old query-param approach (no auth)
      const url = isFirmwareMode
        ? `${API_URL}/downloads?companyName=Firmware`
        : `${API_URL}/downloads`;

      const response = await fetch(url, { headers });

      if (response.status === 401) {
        // Token expired mid-session — surface this to the parent
        console.warn('Download auth token expired');
        return;
      }

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const responseFiles = await response.json();

      // Server already filters by company when token is present,
      // but keep the client-side filter as a safety net for firmware mode
      const filtered = (isFirmwareMode && companyName)
        ? responseFiles.filter(
            f => f.companyName?.toLowerCase() === companyName.toLowerCase()
          )
        : responseFiles;

      setFiles(responseFiles);
      setFilteredFiles(filtered);
    } catch (err) {
      console.error("Error fetching files:", err);
    }
  };

  // ── CHANGE 2: downloadFile now fetches with the token, then triggers a blob download
  // Old code used window.location.href = url, which can't set headers — meaning
  // the /download/* endpoint had no way to verify the user.
  // New code fetches the file as a blob with the Authorization header, creates
  // a temporary object URL, and clicks it — same user experience, but authenticated.
  const downloadFile = async (filename) => {
    if (downloading === filename) return; // prevent double-click
    setDownloading(filename);
    setDownloadPercent(0);

    try {
      const headers = {};
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const { ok, status } = await downloadWithProgress(
        `${API_URL}/download/${encodeURIComponent(filename)}`,
        filename,
        { headers, onProgress: setDownloadPercent }
      );

      if (status === 401) {
        alert('Your session has expired. Please sign in again.');
        return;
      }
      if (status === 403) {
        alert('Access denied. You are not authorized to download this file.');
        return;
      }
      if (!ok) {
        throw new Error(`Download failed: HTTP ${status}`);
      }
    } catch (err) {
      console.error('Download error:', err);
      alert('Download failed. Please try again.');
    } finally {
      setDownloading(null);
    }
  };

  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const toggleSort = (key) => {
    setSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      }
      return { key, dir: "asc" };
    });
  };

  const getSortValue = (file, key) => {
    switch (key) {
      case "name":
        return file.name || "";
      case "serialNumber":
        return file.serialNumber || "";
      case "lastModified": {
        const raw = file.lastModified;
        const ts = typeof raw === "string" || typeof raw === "number" ? Date.parse(raw) : NaN;
        return Number.isFinite(ts) ? ts : String(raw ?? "");
      }
      case "tailNumber":
        return getTailNumber(file);
      case "download":
        return file.name || "";
      default:
        return "";
    }
  };

  const compareValues = (a, b) => {
    if (typeof a === "number" && typeof b === "number") return a - b;
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
  };

  const sortIndicator = (key) => {
    if (sort.key !== key) return "";
    return sort.dir === "asc" ? " ▲" : " ▼";
  };

  const obtainTailName = (filename) => {
    const idx = filename.indexOf(".");
    return idx === -1 ? "-" : filename.slice(0, idx);
  };

  // Older files uploaded before the tailNumber metadata field existed
  // don't have it set — fall back to parsing it out of the filename.
  const getTailNumber = (file) => file.tailNumber || obtainTailName(file.name || "");

  // ======================
  //  Firmware Table View
  // ======================
  if (isFirmwareMode) {
    const firmwareRows = filteredFiles
      .filter((file) => file.name.toLowerCase().includes(searchTerm.toLowerCase()))
      .slice()
      .sort((a, b) => {
        const av = getSortValue(a, sort.key);
        const bv = getSortValue(b, sort.key);
        const cmp = compareValues(av, bv);
        return sort.dir === "asc" ? cmp : -cmp;
      });

    return (
      <div className="firmware-container text-center mt-5">
        <div className="download-header-card">
          <h3 className="firmware-title">Available Firmware Images</h3>
          <input
            type="text"
            placeholder="Search files..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="firmware-search"
          />
        </div>
        <div className="firmware-table-wrapper">
          <table className="firmware-table">
            <thead>
              <tr>
                <th className="sortable" onClick={() => toggleSort("name")}>
                  File{sortIndicator("name")}
                </th>
                <th className="sortable" onClick={() => toggleSort("download")}>
                  Download{sortIndicator("download")}
                </th>
              </tr>
            </thead>
            <tbody>
              {firmwareRows.map((file) => (
                <tr key={file.name}>
                  <td>{file.name.split('/').pop()}</td>
                  <td>
                    <button
                      className="btn btn-warning firmware-btn"
                      onClick={() => downloadFile(file.name)}
                      disabled={downloading === file.name}
                    >
                      {downloading === file.name ? 'Downloading…' : 'Download'}
                    </button>
                    {downloading === file.name && (
                      <div className="download-progress">
                        <ProgressBar barHeight={6} progressPercentage={downloadPercent} />
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ======================
  //  Regular Download Table
  // ======================
  const rows = filteredFiles
    .filter((file) => file.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .slice()
    .sort((a, b) => {
      const av = getSortValue(a, sort.key);
      const bv = getSortValue(b, sort.key);
      const cmp = compareValues(av, bv);
      return sort.dir === "asc" ? cmp : -cmp;
    });

  return (
    <div className="download-container text-center mt-5">
      <div className="download-header-card">
        <h3 className="download-title">Uploaded Files</h3>
        <input
          type="text"
          placeholder="Search files..."
          value={searchTerm}
          onChange={handleSearchChange}
          className="search-file"
        />
      </div>
      <div className="download-table-wrapper">
        <table className="download-table">
          <thead>
            <tr>
              <th className="sortable" onClick={() => toggleSort("name")}>
                File{sortIndicator("name")}
              </th>
              <th className="sortable" onClick={() => toggleSort("serialNumber")}>
                Serial Number{sortIndicator("serialNumber")}
              </th>
              <th
                className="upload-date-col sortable"
                onClick={() => toggleSort("lastModified")}
              >
                Upload Date{sortIndicator("lastModified")}
              </th>
              <th className="sortable" onClick={() => toggleSort("tailNumber")}>
                Tail Number{sortIndicator("tailNumber")}
              </th>
              <th className="sortable" onClick={() => toggleSort("download")}>
                Download{sortIndicator("download")}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((file) => (
              <tr key={file.name}>
                <td data-label="File">{file.name.split('/').pop()}</td>
                <td data-label="Serial Number">{file.serialNumber || "Unknown"}</td>
                <td className="upload-date-col" data-label="Upload Date">{file.lastModified}</td>
                <td data-label="Tail Number">{getTailNumber(file)}</td>
                <td data-label="Download">
                  <button
                    className="btn btn-warning"
                    onClick={() => downloadFile(file.name)}
                    disabled={downloading === file.name}
                  >
                    {downloading === file.name ? 'Downloading…' : 'Download'}
                  </button>
                  {downloading === file.name && (
                    <div className="download-progress">
                      <ProgressBar barHeight={6} progressPercentage={downloadPercent} />
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Download;
